import { BsArrowsFullscreen, BsPeople } from 'react-icons/bs';
import { FaStar, FaArrowRight } from 'react-icons/fa';
import type { Room as RoomType } from '../types';

interface RoomProps {
  room: RoomType;
  onSelect?: () => void;
}

export default function Room({ room, onSelect }: RoomProps) {
  const { id, name, image, size, maxPerson, description, price } = room;

  return (
    <article className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 group overflow-hidden">
      {/* Image Container */}
      <div 
        className="relative overflow-hidden cursor-pointer"
        onClick={onSelect}
      >
        <img
          src={image}
          alt={name}
          className="w-full h-[280px] object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Price Badge */}
        <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-full shadow-lg">
          <span className="font-primary font-bold text-lg">${price}</span>
          <span className="font-secondary text-xs text-white/80 ml-1">/night</span>
        </div>

        {/* Rating Badge */}
        <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <FaStar className="text-yellow-400 text-xs" />
          <span className="font-secondary text-xs font-medium">4.9</span>
          <span className="text-white/60 text-xs">(128 reviews)</span>
        </div>

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/90 backdrop-blur-sm text-gray-800 px-6 py-2 rounded-full text-sm font-medium transform translate-y-4 group-hover:translate-y-0">
            View Details
          </span>
        </div>
      </div>

      {/* Room Specifications Badge */}
      <div className="bg-gradient-to-r from-gray-50 to-white shadow-md max-w-[280px] mx-auto h-[56px] -translate-y-1/2 flex justify-center items-center rounded-xl border border-gray-100">
        <div className="flex justify-between w-[85%]">
          <div className="flex items-center gap-x-2">
            <BsArrowsFullscreen className="text-blue-600 text-[14px]" />
            <span className="flex gap-x-1 text-gray-700 font-secondary text-sm">
              <span className="text-gray-400">Size</span>
              <span className="font-medium">{size}m²</span>
            </span>
          </div>
          <div className="w-px h-6 bg-gray-200" />
          <div className="flex items-center gap-x-2">
            <BsPeople className="text-blue-600 text-[16px]" />
            <span className="flex gap-x-1 text-gray-700 font-secondary text-sm">
              <span className="text-gray-400">Max</span>
              <span className="font-medium">{maxPerson}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Room Details */}
      <div className="text-center px-6 -mt-4 pb-6">
        <h3 
          className="font-primary text-2xl font-bold mb-2 text-gray-800 cursor-pointer hover:text-blue-600 transition-colors duration-200"
          onClick={onSelect}
        >
          {name}
        </h3>
        
        <div className="flex items-center justify-center gap-1 mb-3">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} className="text-yellow-400 text-sm" />
          ))}
        </div>

        <p className="max-w-[300px] mx-auto mb-5 text-gray-500 font-secondary text-sm leading-relaxed line-clamp-2">
          {description.length > 80 ? `${description.slice(0, 80)}...` : description}
        </p>

        <div className="flex items-center justify-center gap-2">
          <button
            onClick={onSelect}
            className="group/btn px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all duration-300 font-primary text-sm tracking-wider uppercase shadow-md hover:shadow-xl hover:scale-105 transform flex items-center gap-2"
            aria-label={`View ${name} starting from $${price}`}
          >
            <span>View Room</span>
            <FaArrowRight className="text-sm group-hover/btn:translate-x-1 transition-transform duration-300" />
          </button>
        </div>
      </div>
    </article>
  );
}